import type { StyleProp, TextStyle, ViewStyle } from 'react-native';

import React, { FC, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Button, Icon, Typography } from '@packages/ui/shared';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import colors from '@packages/core/styles/colors';
import { GestureResponderEvent, StyleSheet } from 'react-native';

/** How the header is drawn: as today, or as the design system's Section Header. */
export type DetailHeaderLook = 'default' | 'tokens';

interface BreadcrumbModel {
    title: string;
    to: string;
}

export interface DetailViewHeaderProps {
    breadcrumbs?: BreadcrumbModel[];
    children?: React.ReactNode;
    rightSideItems?: React.ReactNode[];
    hasBack?: boolean;
    subtitle?: string | React.ReactNode;
    disableBackButton?: boolean;
    /** The look of the header; the default keeps today's spacing and type. */
    look?: DetailHeaderLook;
}

const useDetailViewHeaderStyles = createUseStyles({
    header: ({ hasBreadcrumbs }: { hasBreadcrumbs: boolean }) => ({
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 30px',
        // flexDirection: 'column',
        // justifyContent: 'center',
        gap: '30px',
        height: hasBreadcrumbs ? 100 : 80,
        justifyContent: 'center',
    }),
});

export const DetailViewHeader: FC<DetailViewHeaderProps> = ({ breadcrumbs, hasBack, children }) => {
    const navigate = useNavigate();
    const classes = useDetailViewHeaderStyles({
        hasBreadcrumbs: !!(breadcrumbs && breadcrumbs.length > 0),
    });

    const onBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return (
        <div className={classes.header}>
            {hasBack && (
                <div>
                    <Icon name="arrow-back" onPress={onBack} />
                </div>
            )}
            <div style={{ flex: 1 }}>
                {breadcrumbs && (
                    <div className="mb-1 w-100">
                        {breadcrumbs.map((breadcrumb, index) => {
                            const isLast = index === breadcrumbs.length - 1;

                            if (isLast) {
                                return (
                                    <Typography key={index} variant="label">
                                        {breadcrumb.title}
                                    </Typography>
                                );
                            } else {
                                return (
                                    <span key={index}>
                                        <Typography variant="labelRegular">
                                            <Link to={breadcrumb.to}>{breadcrumb.title}</Link>
                                        </Typography>
                                        <Typography variant="labelRegular" color="grayFive">
                                            &nbsp;&nbsp;/&nbsp;&nbsp;
                                        </Typography>
                                    </span>
                                );
                            }
                        })}
                    </div>
                )}
                <div className="w-100">{children}</div>
            </div>
        </div>
    );
};

const useDetailHeaderStyles = createUseStyles({
    header: {
        alignItems: 'center',
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        height: 88,
        justifyContent: 'space-between',
        padding: '16px 24px',
    },
    headerDefault: {
        gap: '20px',
    },
    headerTokens: {
        gap: '16px',
    },
    leftSection: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
    },
    leftSectionDefault: {
        gap: '20px',
    },
    leftSectionTokens: {
        gap: '16px',
    },
    breadcrumbs: {
        alignItems: 'center',
        display: 'flex',
    },
    breadcrumbsDefault: {
        paddingBottom: '4px',
    },
    breadcrumbsTokens: {
        gap: '8px',
    },
    breadcrumbTokens: {
        display: 'flex',
        gap: '8px',
    },
    linkTokens: {
        color: colors.blueLink,
    },
    rightSideItems: {
        alignItems: 'center',
        display: 'flex',
        gap: '15px',
    },
});

const styles = StyleSheet.create({
    backButtonTokens: {
        borderRadius: 4,
        height: 40,
        paddingHorizontal: 8,
        paddingVertical: 8,
        width: 40,
    },
    backSlash: {
        color: colors.textDarkSecondary,
        fontWeight: '400',
        paddingLeft: 7,
        paddingRight: 7,
    },
    backSlashTokens: {
        color: colors.textDarkSecondary,
        fontWeight: '400',
        lineHeight: 24,
    },
    breadcrumb: {
        fontWeight: '400',
    },
    breadcrumbTokens: {
        fontWeight: '400',
        lineHeight: 24,
    },
    subTitle: {
        fontWeight: '600',
    },
    subTitleTokens: {
        fontWeight: '600',
        lineHeight: 32,
    },
    iconSpacing: {
        marginRight: 16,
    },
});

/** The parts that differ between the looks: layout classes, text styles, the back button and the separator. */
type DetailHeaderLookParts = {
    /** The size and spacing of the back button. */
    backButtonStyle: StyleProp<ViewStyle>;
    /** The class of one crumb with its separator, when the look spaces them. */
    breadcrumbClassName?: string;
    /** The text style of every crumb. */
    breadcrumbStyle: StyleProp<TextStyle>;
    /** The class of the breadcrumb row. */
    breadcrumbsClassName: string;
    /** The class of the bar. */
    headerClassName: string;
    /** The class of the back button and texts section. */
    leftSectionClassName: string;
    /** The class of a crumb link, when the look colors it. */
    linkClassName?: string;
    /** The text between two crumbs. */
    separator: string;
    /** The text style of the separator. */
    separatorStyle: StyleProp<TextStyle>;
    /** The text style of the title. */
    subtitleStyle: StyleProp<TextStyle>;
};

/**
 * The parts of every look. The default is the header as it has always been drawn; the tokens look is
 * the design system's Section Header: a 40px tertiary back button, 16px gaps, Body 15/24 crumbs 8px
 * apart with the Link/Main color, and a Heading03 21/32 title right under them.
 *
 * @param classes - the layout classes of the header
 * @returns the parts by look
 */
function detailHeaderLookParts(
    classes: ReturnType<typeof useDetailHeaderStyles>
): Record<DetailHeaderLook, DetailHeaderLookParts> {
    return {
        default: {
            backButtonStyle: styles.iconSpacing,
            breadcrumbStyle: styles.breadcrumb,
            breadcrumbsClassName: classes.breadcrumbsDefault,
            headerClassName: classes.headerDefault,
            leftSectionClassName: classes.leftSectionDefault,
            separator: '\u00a0/\u00a0',
            separatorStyle: styles.backSlash,
            subtitleStyle: styles.subTitle,
        },
        tokens: {
            backButtonStyle: styles.backButtonTokens,
            breadcrumbClassName: classes.breadcrumbTokens,
            breadcrumbStyle: styles.breadcrumbTokens,
            breadcrumbsClassName: classes.breadcrumbsTokens,
            headerClassName: classes.headerTokens,
            leftSectionClassName: classes.leftSectionTokens,
            linkClassName: classes.linkTokens,
            separator: '/',
            separatorStyle: styles.backSlashTokens,
            subtitleStyle: styles.subTitleTokens,
        },
    };
}

export const DetailHeader: FC<DetailViewHeaderProps> = ({
    breadcrumbs = [],
    subtitle,
    rightSideItems = [],
    children,
    hasBack,
    disableBackButton = false,
    look = 'default',
}) => {
    const navigate = useNavigate();
    const classes = useDetailHeaderStyles();
    const parts = detailHeaderLookParts(classes)[look];

    const handleNavigation = useCallback(
        (event: GestureResponderEvent) => {
            event.preventDefault();
            if (breadcrumbs.length > 1) {
                const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
                if (previousBreadcrumb?.to) {
                    navigate(previousBreadcrumb.to);
                    return;
                }
            }
            navigate(-1);
        },
        [breadcrumbs, navigate]
    );

    return (
        <div className={classNames(classes.header, parts.headerClassName)}>
            <div className={classNames(classes.leftSection, parts.leftSectionClassName)}>
                {hasBack && (
                    <Button
                        onPress={handleNavigation}
                        style={parts.backButtonStyle}
                        accessibilityRole="button"
                        accessibilityLabel="arrow-back"
                        testID="back-icon"
                        variant="ghost-blue"
                        disabled={disableBackButton}
                        isLoading={disableBackButton}
                    >
                        <Icon name="arrow-back" color="textDarkSecondary" />
                    </Button>
                )}
                <div>
                    {breadcrumbs.length > 0 && (
                        <div className={classNames(classes.breadcrumbs, parts.breadcrumbsClassName)}>
                            {breadcrumbs.map((breadcrumb, index) => {
                                const isLast = index === breadcrumbs.length - 1;

                                return (
                                    <div className={parts.breadcrumbClassName} key={breadcrumb.to}>
                                        {isLast ? (
                                            <Typography variant="label" style={parts.breadcrumbStyle}>
                                                {breadcrumb.title}
                                            </Typography>
                                        ) : (
                                            <>
                                                <Typography variant="labelRegular" style={parts.breadcrumbStyle}>
                                                    <Link className={parts.linkClassName} to={breadcrumb.to}>
                                                        {breadcrumb.title}
                                                    </Link>
                                                </Typography>
                                                <Typography
                                                    style={parts.separatorStyle}
                                                    variant="labelRegular"
                                                    color="textDarkSecondary"
                                                >
                                                    {parts.separator}
                                                </Typography>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {subtitle && (
                        <Typography variant="h3" style={parts.subtitleStyle}>
                            {subtitle}
                        </Typography>
                    )}
                </div>
            </div>
            {children && <div className="w-100">{children}</div>}
            {rightSideItems.length ? (
                <div className={classes.rightSideItems}>
                    {rightSideItems.map((item, index) => (
                        <React.Fragment key={index}>{item}</React.Fragment>
                    ))}
                </div>
            ) : null}
        </div>
    );
};
